import { v4 as uuidv4 } from 'uuid';

function generateUniqueId() {
  return uuidv4();
}

function removeExternalLoader() {
  const loaderEl = document.querySelector('.js-external-loader');

  if (!loaderEl) {
    return;
  }

  loaderEl.parentNode.removeChild(loaderEl);
}

function getSizeFromResizeObserverEntry(entry) {
  if (!entry.contentBoxSize) {
    return {
      width: entry.contentRect.width,
      height: entry.contentRect.height,
    };
  }

  const {
    inlineSize: width,
    blockSize: height,
  } = Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0] : entry.contentBoxSize;

  return { width, height };
}

const tagBody = '(?:[^"\'>]|"[^"]*"|\'[^\']*\')*';

const tagOrComment = new RegExp(
  `${'<(?:'
  + '!--(?:(?:-*[^->])*--+|-?)' // Comment body.
  + '|script\\b'}${tagBody}(/|>[\\s\\S]*?</script\\s*)` // <script...>...</script> & <script.../>
  + `|style\\b${tagBody}(/|>[\\s\\S]*?</style\\s*)` // <style...>...<style> & <style.../>
  + `|iframe\\b${tagBody}(/|>[\\s\\S]*?</iframe\\s*)` // <iframe...>...<iframe> & <iframe.../>
  + ')>',
  'gi',
);

const sanitizeHTML = html => html.replace(tagOrComment, '');

async function loadScript(url, attributes = {}) {
  await new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;

    Object.keys(attributes).forEach((key) => {
      script[key] = attributes[key];
    });

    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject());

    document.body.appendChild(script);
  });
}

function getTransportMode(mode) {
  switch (mode) {
    case 'walking':
      return 'WALK';
    case 'bike':
      return 'BICYCLE';
    case 'e-bike':
      return 'E-BIKE';
    case 'ov':
      return 'WALK,TRANSIT';
    case 'bike_ov':
      return 'BICYCLE_PARK,TRANSIT';
    case 'car_ov':
      return 'CAR_PARK,TRANSIT';
    case 'car':
      return 'CAR';
    default:
      return 'BICYCLE';
  }
}

const numberFormatter = new Intl.NumberFormat('nl-BE', {
  maximumFractionDigits: 2,
});

function formatDistance(distance) {
  if (distance < 1000) {
    return `${Math.round(distance)} m`;
  }

  return `${numberFormatter.format(distance / 1000)} km`;
}

function formatTime(duration, fallbackDuration = 0) {
  const totalMinutes = Math.round((duration || fallbackDuration) / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours} uur ${minutes} min`;
  } if (hours > 0) {
    return `${hours} uur`;
  } 
  return `${minutes} min`;
}

function companyHasAddress(company) {
  if (!company) {
    return false;
  }

  return !!company.properties.CRAB_STR;
}

function isDevelopmentEnvironment() {
  return import.meta.env.VITE_NAZKA_ENV === 'development';
}

function formatCompanyIdentificationNumber(companyIdentificationNumber) {
  let value = companyIdentificationNumber.replace(/\D/g, '');
  if (value.length > 4) value = `${value.slice(0, 4)}.${value.slice(4)}`;
  if (value.length > 8) value = `${value.slice(0, 8)}.${value.slice(8)}`;
  value = value.length <= 12 ? value : value.slice(0, 12);
  return value;
}

function shouldUseDateForTransportType(transportType) {
  return ['ov', 'bike_ov', 'car_ov'].includes(transportType);
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(email) {
  return email && EMAIL_PATTERN.test(email);
}

function formatTimestamp(timestamp) {
  const date = new Date(timestamp);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}-${month}-${year} om ${hours}:${minutes}`;
}

function formatHoursMinutes(timestamp) {
  const date = new Date(timestamp);

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
}

function normalizeText(text) {
  return text?.replace(/\\[tnrfv]/g, '').trim() || text;
}

export {
  companyHasAddress,
  formatCompanyIdentificationNumber,
  formatDistance,
  formatTime,
  generateUniqueId,
  getSizeFromResizeObserverEntry,
  getTransportMode,
  isDevelopmentEnvironment,
  isValidEmail,
  loadScript,
  removeExternalLoader,
  sanitizeHTML,
  shouldUseDateForTransportType,
  formatTimestamp,
  formatHoursMinutes,
  normalizeText,
}